/**
 * Tanjong Life · L6 · the process runs a claim while the room watches.
 *
 * Unscoped, so it fires from the Wizard, from a process page and from a case list. The
 * discriminator is `on claim clm-` — the build sentence for the same process
 * ("build a process for fraud detection and SIU triage") contains `fraud detection` and
 * `siu triage` too, and this scenario is only reachable because it is registered BEFORE
 * `tanjong-build-fraud`. Do not reorder the two.
 *
 * The payload rides behind a dynamic import so the whole run — the case, its feed, the
 * five connector traces, the two inbox items, the trigger fire and the patched counters —
 * is its own chunk and loads only when this step is reached. Its stamps are story tokens,
 * resolved by `runCase` against the click, so the run happened in the thirty-one seconds
 * before "now" whatever day the demo is given on.
 *
 * What it deliberately does NOT do: refer the claim. The run ends at a person.
 */
import type { RunPayload } from '@wand/shared/dataset/run_payload.js';
import type { Scenario } from '../director.js';

export const tanjongRunFraud: Scenario = {
  id: 'tanjong-run-fraud',
  orgId: 'tanjong-life',
  keywords: ['run fraud detection', 'on claim clm-', 'run the fraud process', 'score clm-3310'],
  steps: [
    {
      do: 'say',
      text: 'Starting the process on CLM-3310. It reads, it scores, it ranks and it stops.',
      delay: 400,
    },
    {
      do: 'thinking',
      steps: [
        {
          tool: {
            name: 'trigger.fired',
            input: 'Claims administration · claim entered assessment',
            output: 'CLM-3310 · Daniel Teo · P-70418 · SGD 31,500',
          },
        },
        {
          tool: {
            name: 'claims.read',
            input: 'claim=CLM-3310 · include history',
            output: '5 claims through one clinic in 96 days · two years of continuous cover',
          },
        },
        {
          tool: {
            name: 'providers.read',
            input: 'provider=PRV-20481 · panel tenure',
            output:
              'Marine Parade Physio & Rehab · on the panel 3 years · the same cluster FRD-3302 was scored against',
          },
        },
        {
          thought:
            'Two of the nine terms move against the standing example, and both move because the evidence behind them is different: five unrelated claims in ninety-six days rather than four in ninety-one, and two years of cover rather than fourteen. Nothing else changes, so the arithmetic is the arithmetic.',
        },
      ],
      delay: 900,
    },
    {
      do: 'test',
      title: 'Running the process…',
      items: [
        { label: 'Claims administration', sub: 'Claim and history read under the process identity' },
        {
          label: 'Policy & benefits',
          sub: 'Schedule read · the identity number never leaves the system',
        },
        { label: 'Panel & provider network', sub: 'Provider and panel tenure resolved' },
        { label: 'SIU queue placement', sub: 'Ranked in order · nothing referred' },
        { label: 'Open an investigation', sub: 'Refused for the analyst — a named officer decides' },
      ],
      result: 'Run complete — with a person',
      delay: 1200,
    },
    {
      do: 'run',
      payload: () =>
        import('../assets/tanjong-frd-3310-run.json').then((m) => m.default as unknown as RunPayload),
      summary: 'FRD-3310 created — scored 84 against a threshold of 70, and referred nothing',
      delay: 1800,
    },
    {
      do: 'say',
      text: 'Eighty-four against a threshold of seventy, and it stopped. Five things became true in the last half-minute and every one of them is somewhere you can open: the case is sitting at its disposition gate, the approval is in the Inbox at the Financial Crime desk, the receipt for the memo and the indicator workbook is in the mailroom, five traces are in the integration audit with one of them refused, and the claims-administration trigger fired just now. Nothing was referred, because referring is not something this process can do.',
      sources: ['FRD-3310 · awaiting a person', 'EX-3310-01..05 · one blocked'],
      delay: 600,
    },
    {
      // The one step in this act that leaves the platform. The workflow — not the agent,
      // which has no authority to raise anything — posts the referral to the Financial
      // Crime desk's own channel, with the score, the threshold, the three heaviest terms
      // and a link back to the case. Everything about it is real: the workspace, the
      // message, the round trip and the trace it leaves. If the channel is not connected
      // it says so in a sentence and the room decides on screen, as it always could.
      do: 'notify',
      beat: 'fraud-referral',
      caseRef: 'FRD-3310',
      summary: 'Referral posted to the Financial Crime desk channel',
      sent: 'And it has gone out to the desk. That message left this platform a moment ago and landed in the Financial Crime channel — the score, the threshold it was measured against, the three heaviest terms, and one button. Whoever is on the desk can open the investigation from their phone, and when they do, this case will move in front of you. Note who it went to: a named colleague on the desk that owns the decision. It cannot reach a policyholder, a provider or an adviser, and that is wired rather than agreed.',
      degraded:
        'The desk channel is not connected in this environment, so nothing left the platform — and that is the whole of the failure. The referral is in the Inbox at the Financial Crime desk exactly as it would be, and the decision is taken on this screen. The channel is how the desk hears about it sooner; it was never how the decision gets made.',
      delay: 900,
    },
    {
      do: 'link',
      label: 'Open FRD-3310 — the case and its gate',
      href: '/cases/FRD-3310',
      delay: 600,
    },
    {
      do: 'link',
      label: 'Open the approval in the Inbox',
      href: '/inbox?itemId=tl-fraud-triage-3310',
      delay: 500,
    },
  ],
};
